<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import useApi from '../../Composables/useApi'
import PlaceholderContent from '../UI/Loading/PlaceholderContent.vue'
import DocumentSignerItem from './DocumentSignerItem.vue'
import type { DocumentSigner } from '../../../../Types/document'
import { useDocumentSignApi } from '../../../../Composables/DocumentSign/useDocumentSignApi'

interface Props {
  documentSignId: number
  contractId: number
  disabled?: boolean
}

defineExpose({
  loadSigners,
})
const props = defineProps<Props>()

const { getJSON } = useApi()
const { getSigners } = useDocumentSignApi()
const signers = ref<DocumentSigner[]>([])
const loading = ref<boolean>(false)

const validatorsNotSigned = computed<DocumentSigner[]>(() => {
  return signers.value.filter((signer: DocumentSigner) => signer.validator && !signer.signedAt)
})

function getAvailableSignersToSign(): DocumentSigner[] {
  if (validatorsNotSigned.value.length > 0) {
    return validatorsNotSigned.value
  }
  return signers.value.filter((signer: DocumentSigner) => !signer.signedAt)
}

function canSignerSign(signer: DocumentSigner): boolean {
  const availableSigners = getAvailableSignersToSign()
  const firstAvailableSigner = availableSigners[0]

  if (!firstAvailableSigner) {
    return false
  }

  if (firstAvailableSigner.validator) {
    return signer.id === firstAvailableSigner.id
  }

  return availableSigners.some((availableSigner: DocumentSigner) => availableSigner.id === signer.id)
}

async function loadSigners(): Promise<void> {
  if (loading.value) {
    return
  }
  loading.value = true
  try {
    signers.value = await getSigners(props.documentSignId)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.documentSignId,
  () => {
    loadSigners()
  },
  { immediate: true },
)
</script>

<template>
  <div>
    <div v-if="loading">
      <PlaceholderContent :rows="4" />
    </div>
    <div v-else>
      <DocumentSignerItem
        v-for="signer in signers"
        :key="`document-signer-${signer.id}`"
        :document-signer="signer"
        :document-sign-id="props.documentSignId"
        :contract-id="props.contractId"
        :disabled="!canSignerSign(signer) || props.disabled"
      />
    </div>
  </div>
</template>
